import { isAfterTrigger, type RuleDefinition, type RuleStep, type RuleTrigger } from '@/types/rules.types';

/**
 * Where a custom playbook is shown: inside the settings page for when it runs, so the settings stay
 * where implementation experts know them. The engine underneath is one ordered list per brand.
 */
export type PlaybookPlace = 'pre-processing' | 'automation' | 'social' | 'sales';

const SALES_EVENTS: readonly RuleTrigger[] = ['after_discount_sent', 'after_discount_used', 'after_products_recommended'];

export const PLACES: Record<PlaybookPlace, { label: string; path: string; triggers: (all: RuleTrigger[]) => RuleTrigger[] }> = {
	'pre-processing': {
		label: 'Support Agent',
		path: '/agents/support',
		triggers: () => ['message_received']
	},
	automation: {
		label: 'Automation',
		path: '/settings/automation',
		triggers: (all) =>
			all.filter((trigger) => trigger === 'ai_answer' || (isAfterTrigger(trigger) && trigger !== 'after_comment_action' && !SALES_EVENTS.includes(trigger)))
	},
	social: { label: 'Social media agent', path: '/agents/social-media', triggers: () => ['social_comment', 'after_comment_action'] },
	sales: { label: 'Sales Agent', path: '/agents/sales?tab=custom-playbooks', triggers: () => ['message_received', ...SALES_EVENTS] }
};

const usesAction = (steps: RuleStep[], action: string): boolean =>
	steps.some((step) => (step.kind === 'action' ? step.action === action : step.kind === 'if' && (usesAction(step.then, action) || usesAction(step.else, action))));

/** The page a playbook belongs on. A message playbook that sets discounts is the Sales Agent's. */
export function placeOf(definition: Pick<RuleDefinition, 'trigger' | 'steps'>): PlaybookPlace {
	const { trigger } = definition;
	if (trigger === 'social_comment' || trigger === 'after_comment_action') return 'social';
	if (SALES_EVENTS.includes(trigger)) return 'sales';
	if (trigger === 'message_received') return usesAction(definition.steps, 'sales_discount') ? 'sales' : 'pre-processing';
	return 'automation';
}

/**
 * Whether a page lists the playbook. Pre-Processing lists every customer-message playbook, discount
 * ones too, since they all share one order and that order has to be changeable in one place.
 */
export const shownOn = (definition: Pick<RuleDefinition, 'trigger' | 'steps'>, place: PlaybookPlace): boolean =>
	(place === 'pre-processing' && definition.trigger === 'message_received') || placeOf(definition) === place;

export const isPlace = (value: string | null): value is PlaybookPlace => value !== null && value in PLACES;
